import { useState, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import type { FilterWellItem } from '@/api/dataVault';

interface Props {
  wells: FilterWellItem[];
  selected: string[];
  onChange: (ids: string[]) => void;
  loading?: boolean;
}

export function WellSelector({ wells, selected, onChange, loading }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const lowerSearch = search.toLowerCase();
  const filtered = wells.filter(
    (w) =>
      w.well_name.toLowerCase().includes(lowerSearch) ||
      w.site_name.toLowerCase().includes(lowerSearch),
  );

  // Group wells by site for display
  const grouped: Record<string, FilterWellItem[]> = {};
  for (const w of filtered) {
    const key = w.site_name || 'Unknown';
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(w);
  }
  const siteNames = Object.keys(grouped).sort();
  const multiSite = siteNames.length > 1;

  const toggle = (id: string) => {
    if (selected.includes(id)) {
      onChange(selected.filter((s) => s !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  const selectedNames = wells
    .filter((w) => selected.includes(w.id))
    .map((w) => w.well_name);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded transition-colors min-w-[160px] max-w-[260px]"
        style={{
          background: 'var(--dv-bg-input)',
          border: '1px solid var(--dv-border-default)',
          color: 'var(--dv-text-primary)',
        }}
      >
        <span className="truncate flex-1 text-left">
          {selected.length === 0
            ? 'ALL WELLS'
            : selected.length === 1
              ? selectedNames[0]
              : `${selected.length} WELLS`}
        </span>
        <ChevronDownIcon className="h-3 w-3 shrink-0" style={{ color: 'var(--dv-text-muted)' }} />
      </button>

      {open && (
        <div
          // Fully-opaque so chart content under the popover doesn't bleed
          // through (matches SiteSelector fix for the same beta issue).
          className="absolute z-50 top-full mt-1 left-0 w-72 rounded shadow-xl max-h-80 flex flex-col"
          style={{
            background: 'var(--dv-bg-card-solid)',
            border: '1px solid var(--dv-border-default)',
          }}
        >
          <div className="p-2" style={{ borderBottom: '1px solid var(--dv-border-subtle)' }}>
            <input
              type="text"
              placeholder="Search wells..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full text-xs px-2 py-1 rounded focus:outline-none"
              style={{
                background: 'var(--dv-bg-input)',
                border: '1px solid var(--dv-border-default)',
                color: 'var(--dv-text-primary)',
              }}
              autoFocus
            />
          </div>
          {selected.length > 0 && (
            <button
              onClick={() => { onChange([]); setSearch(''); }}
              className="px-3 py-1.5 text-xs text-left tracking-wider transition-colors"
              style={{ color: 'var(--dv-text-muted)' }}
            >
              CLEAR ALL
            </button>
          )}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="px-3 py-4 text-xs tracking-wider" style={{ color: 'var(--dv-text-muted)' }}>LOADING...</div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs tracking-wider" style={{ color: 'var(--dv-text-muted)' }}>NO WELLS FOUND</div>
            ) : (
              siteNames.map((siteName) => (
                <div key={siteName}>
                  {multiSite && (
                    <div
                      className="px-3 py-1 text-[10px] tracking-widest uppercase sticky top-0"
                      style={{
                        color: 'var(--dv-text-muted)',
                        background: 'var(--dv-bg-primary)',
                        borderBottom: '1px solid var(--dv-border-subtle)',
                      }}
                    >
                      {siteName}
                    </div>
                  )}
                  {grouped[siteName].map((well) => (
                    <button
                      key={well.id}
                      onClick={() => toggle(well.id)}
                      className="w-full px-3 py-1.5 text-left text-xs transition-colors flex items-center gap-2"
                      style={{
                        color: selected.includes(well.id) ? 'var(--dv-text-primary)' : 'var(--dv-text-secondary)',
                        background: selected.includes(well.id) ? 'var(--dv-bg-card-hover)' : 'transparent',
                      }}
                    >
                      <span
                        className="w-3 h-3 rounded border shrink-0 flex items-center justify-center"
                        style={{
                          borderColor: selected.includes(well.id) ? 'var(--dv-accent-cyan)' : 'var(--dv-border-default)',
                          background: selected.includes(well.id) ? 'rgba(34, 211, 238, 0.15)' : 'transparent',
                        }}
                      >
                        {selected.includes(well.id) && (
                          <span className="w-1.5 h-1.5 rounded-sm" style={{ background: 'var(--dv-accent-cyan)' }} />
                        )}
                      </span>
                      <span className="truncate">{well.well_name}</span>
                      {well.well_type && (
                        <span className="text-[10px] ml-auto shrink-0" style={{ color: 'var(--dv-text-disabled)' }}>{well.well_type}</span>
                      )}
                    </button>
                  ))}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
